import React, { useState } from 'react';
import Seo from '../components/Seo';
import { localBusinessLd } from '../lib/seo';
import { MapPin, Phone, Mail, MessageCircle, Clock, Send, CheckCircle2, Building2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { contact, productOptions } from '../mock/mock';
import { api } from '../lib/api';
import { waDisplay, openWhatsApp } from '../lib/wa';

const initialForm = { name: '', email: '', company: '', product: '', port: '', quantity: '', message: '' };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name';
    if (!form.email.trim()) e.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.message.trim()) e.message = 'Please enter a message';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    api.post('/enquiries', { type: 'contact', ...form })
      .then(() => { setSent(true); setForm(initialForm); })
      .catch(() => { alert('Something went wrong. Please try again or contact us on WhatsApp.'); });
  };

  const info = [
    { icon: Building2, label: 'Head Office', value: contact.headOffice },
    { icon: MapPin, label: 'Rice Mill & Processing Facility', value: contact.mill },
    { icon: Phone, label: 'Phone', value: contact.phone, href: `tel:${contact.phoneRaw}` },
    { icon: MessageCircle, label: 'WhatsApp', value: waDisplay(), onClick: () => openWhatsApp() },
    { icon: Mail, label: 'Email', value: contact.email, href: `mailto:${contact.email}` },
    { icon: MapPin, label: "North America's Office", value: `${contact.naOffice} · ${contact.naPhone}` },
    { icon: Clock, label: 'Office Hours', value: contact.hours },
  ];

  return (
    <div>
      <Seo title="Contact Us — Karachi, Pakistan & Canada" description="Get in touch with Kassam Trading Company. Head office in Karachi, mill at Port Qasim, and North America office in Waterloo, Canada. Reach us by email, phone or WhatsApp." path="/contact" jsonLd={localBusinessLd} />
      <PageHero
        title="Get in Touch with Kassam Trading Company"
        subtitle="Reach us for rice, salt and sesame seeds export inquiries. Call, email or WhatsApp us today for a fast response."
        crumb="Contact"
      />

      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-10">
        {/* Info */}
        <Reveal className="lg:col-span-2 space-y-5">
          {info.map((x) => (
            <div key={x.label} className="flex gap-4">
              <div className="h-11 w-11 shrink-0 grid place-items-center rounded-lg bg-ktc-green text-ktc-goldLight">
                <x.icon size={19} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ktc-gold font-semibold">{x.label}</div>
                {x.onClick ? (
                  <button type="button" onClick={x.onClick} data-testid="contact-whatsapp-button" className="text-sm text-ktc-charcoal/80 hover:text-ktc-green text-left">{x.value}</button>
                ) : x.href ? (
                  <a href={x.href} target="_blank" rel="noreferrer" className="text-sm text-ktc-charcoal/80 hover:text-ktc-green">{x.value}</a>
                ) : (
                  <div className="text-sm text-ktc-charcoal/80">{x.value}</div>
                )}
              </div>
            </div>
          ))}
        </Reveal>

        {/* Form */}
        <Reveal delay={100} className="lg:col-span-3">
          <div className="bg-ktc-ivoryLight rounded-2xl border border-ktc-cream p-7 md:p-9">
            {sent ? (
              <div className="text-center py-14">
                <CheckCircle2 className="mx-auto text-ktc-green" size={56} />
                <h3 className="font-serif text-3xl font-semibold text-ktc-green mt-4">Message Sent</h3>
                <p className="text-ktc-charcoal/70 mt-3">Thank you for reaching out. Our team will respond shortly.</p>
                <button onClick={() => setSent(false)} className="mt-6 text-ktc-gold font-semibold link-underline">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <h2 className="font-serif text-3xl font-semibold text-ktc-green mb-6">Message Us</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name" required error={errors.name}>
                    <input value={form.name} onChange={(e) => update('name', e.target.value)} className="ktc-input" placeholder="Your name" />
                  </Field>
                  <Field label="Email" required error={errors.email}>
                    <input value={form.email} onChange={(e) => update('email', e.target.value)} className="ktc-input" placeholder="you@company.com" />
                  </Field>
                  <Field label="Company">
                    <input value={form.company} onChange={(e) => update('company', e.target.value)} className="ktc-input" placeholder="Company name" />
                  </Field>
                  <Field label="Product Required">
                    <select value={form.product} onChange={(e) => update('product', e.target.value)} className="ktc-input">
                      <option value="">Select a product</option>
                      {productOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Destination Port">
                    <input value={form.port} onChange={(e) => update('port', e.target.value)} className="ktc-input" placeholder="e.g. Jebel Ali" />
                  </Field>
                  <Field label="Quantity Required (Tons)">
                    <input value={form.quantity} onChange={(e) => update('quantity', e.target.value)} className="ktc-input" placeholder="e.g. 100" />
                  </Field>
                </div>
                <Field label="Message" required error={errors.message}>
                  <textarea value={form.message} onChange={(e) => update('message', e.target.value)} rows={4} className="ktc-input resize-none" placeholder="How can we help?" />
                </Field>
                <button type="submit" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-8 py-3.5 rounded-md mt-3 transition-colors">
                  <Send size={17} /> Send Message
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
};

const Field = ({ label, required, error, children }) => (
  <label className="block mb-4">
    <span className="text-sm font-medium text-ktc-charcoal/80">{label}{required && <span className="text-ktc-gold"> *</span>}</span>
    <div className="mt-1.5">{children}</div>
    {error && <span className="text-xs text-red-600 mt-1 block">{error}</span>}
  </label>
);

export default Contact;
